import { useState } from 'react';
import { 
  Send,
  CheckCircle2,
  MapPin,
  Phone,
  Mail,
  Clock,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    is_vtc_driver: false,
    platform: '',
    rental_duration: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, is_vtc_driver: checked }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setIsSubmitted(true);
      toast.success('Demande envoyée avec succès !');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-5 h-5" />,
      label: "Adresse",
      value: "328 Boulevard Paul Raphel\n13730 Saint-Victoret"
    },
    {
      icon: <Phone className="w-5 h-5" />,
      label: "Téléphone",
      value: "06 20 20 29 96",
      href: "tel:0620202996"
    },
    {
      icon: <Mail className="w-5 h-5" />,
      label: "Email",
      value: "luxeocar@gmail.com",
      href: "mailto:luxeocar@gmail.com"
    },
    {
      icon: <Clock className="w-5 h-5" />,
      label: "Horaires",
      value: "Lun-Ven: 9h-18h, Sam: 10h-16h"
    }
  ];

  if (isSubmitted) {
    return (
      <div data-testid="contact-page" className="min-h-screen pt-32 pb-20 bg-[#050505]">
        <SEO 
          title="Contact | Demande de Location Véhicule VTC | Luxeo Car Marseille"
          description="Contactez Luxeo Car pour louer un véhicule VTC à Marseille. Tesla Model Y, Ford Mustang Mach-E, Mercedes Classe V. Réponse sous 24h. Tél: 06 20 20 29 96."
          keywords="contact Luxeo Car, demande location VTC, louer Tesla Marseille, réserver véhicule Uber Bolt"
          canonical="/contact"
        />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div data-testid="success-message" className="card-luxeo p-12 gold-glow">
            <div className="w-20 h-20 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-8">
              <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
            </div>
            <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-4">
              Demande envoyée !
            </h2>
            <p className="font-['Manrope'] text-[#A1A1AA] mb-8">
              Merci pour votre intérêt pour Luxeo Car. Notre équipe vous contactera dans les plus brefs délais pour discuter de votre projet.
            </p>
            <button
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  full_name: '',
                  phone: '',
                  email: '',
                  is_vtc_driver: false,
                  platform: '',
                  rental_duration: '',
                  message: ''
                });
              }}
              data-testid="new-request-btn"
              className="btn-secondary"
            >
              Nouvelle demande
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="overflow-hidden">
      <SEO 
        title="Contact | Demande de Location Véhicule VTC | Luxeo Car Marseille"
        description="Contactez Luxeo Car pour louer un véhicule VTC à Marseille. Tesla Model Y, Ford Mustang Mach-E, Mercedes Classe V. Réponse sous 24h. Tél: 06 20 20 29 96."
        keywords="contact Luxeo Car, demande location VTC, louer Tesla Marseille, réserver véhicule Uber Bolt"
        canonical="/contact"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block font-['Outfit'] text-[#D4AF37] text-sm font-medium uppercase tracking-wider mb-4">
              Contact
            </span>
            <h1 
              data-testid="contact-hero-title"
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Demande de{' '}
              <span className="text-gradient-gold">location</span>
            </h1>
            <p className="font-['Manrope'] text-base lg:text-lg text-[#A1A1AA]">
              Remplissez le formulaire ci-dessous et notre équipe vous recontactera rapidement.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section data-testid="form-section" className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-6">
                  Nos coordonnées
                </h2>
                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <div
                      key={index}
                      data-testid={`contact-info-${index}`}
                      className="flex items-start gap-4"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
                        {info.icon}
                      </div>
                      <div>
                        <p className="font-['Manrope'] text-[#A1A1AA] text-sm mb-1">
                          {info.label}
                        </p>
                        {info.href ? (
                          <a
                            href={info.href}
                            className="font-['Manrope'] text-white hover:text-[#D4AF37] transition-colors whitespace-pre-line"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="font-['Manrope'] text-white whitespace-pre-line">
                            {info.value}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Company Info */}
              <div className="card-luxeo p-6">
                <h3 className="font-['Outfit'] text-lg font-semibold text-white mb-3">
                  SARL LUXEO CAR
                </h3>
                <div className="font-['Manrope'] text-[#A1A1AA] text-sm space-y-1">
                  <p>Capital : 5 000 €</p>
                  <p>SIRET : 989 521 497 00010</p>
                  <p>RCS Aix-en-Provence</p>
                </div>
              </div>

              {/* Additional Info */}
              <div className="card-luxeo p-6">
                <h3 className="font-['Outfit'] text-lg font-semibold text-white mb-3">
                  Réponse rapide
                </h3>
                <p className="font-['Manrope'] text-[#A1A1AA] text-sm leading-relaxed">
                  Notre équipe traite les demandes sous 24h. Préparez votre carte VTC pour accélérer le processus.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <form
                onSubmit={handleSubmit}
                data-testid="lead-form"
                className="card-luxeo p-8 sm:p-10"
              >
                <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-8">
                  Formulaire de demande
                </h2>

                <div className="space-y-6">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-white">
                      Nom complet *
                    </Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder="Jean Dupont"
                      data-testid="input-fullname"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                    />
                  </div>

                  {/* Phone & Email Row */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white">
                        Téléphone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+33 6 00 00 00 00"
                        data-testid="input-phone"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="jean@example.com"
                        data-testid="input-email"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                      />
                    </div>
                  </div>

                  {/* VTC Driver Checkbox */}
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="is_vtc_driver"
                      checked={formData.is_vtc_driver}
                      onCheckedChange={handleCheckboxChange}
                      data-testid="checkbox-vtc"
                      className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                    />
                    <Label htmlFor="is_vtc_driver" className="text-white cursor-pointer">
                      Je suis chauffeur VTC
                    </Label>
                  </div>

                  {/* Platform & Vehicle Row */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-white">
                        Plateforme utilisée
                      </Label>
                      <Select
                        value={formData.platform}
                        onValueChange={(value) => handleSelectChange('platform', value)}
                      >
                        <SelectTrigger
                          data-testid="select-platform"
                          className="bg-white/5 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        >
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10">
                          <SelectItem value="uber" className="text-white focus:bg-white/10">Uber</SelectItem>
                          <SelectItem value="bolt" className="text-white focus:bg-white/10">Bolt</SelectItem>
                          <SelectItem value="both" className="text-white focus:bg-white/10">Les deux</SelectItem>
                          <SelectItem value="other" className="text-white focus:bg-white/10">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">
                        Véhicule souhaité
                      </Label>
                      <Select
                        value={formData.rental_duration}
                        onValueChange={(value) => handleSelectChange('rental_duration', value)}
                      >
                        <SelectTrigger
                          data-testid="select-duration"
                          className="bg-white/5 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                        >
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10">
                          <SelectItem value="tesla-model-y" className="text-white focus:bg-white/10">Tesla Model Y</SelectItem>
                          <SelectItem value="mustang-mach-e" className="text-white focus:bg-white/10">Ford Mustang Mach-E</SelectItem>
                          <SelectItem value="mercedes-classe-v" className="text-white focus:bg-white/10">Mercedes Classe V</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">
                      Message (optionnel)
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Précisez votre demande ou posez vos questions..."
                      rows={4}
                      data-testid="input-message"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="submit-btn"
                    className="btn-primary w-full text-base font-bold py-4 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Demander un véhicule
                      </>
                    )}
                  </button>

                  <p className="font-['Manrope'] text-[#52525B] text-xs text-center">
                    En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe concernant votre demande de location.
                    Vous pouvez aussi nous contacter directement à{' '}
                    <a href="mailto:luxeocar@gmail.com" className="text-[#D4AF37] hover:underline">
                      luxeocar@gmail.com
                    </a>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
